/**
 * Task-template data layer — TanStack Query hooks over the #983/#984
 * worked-example template API (`/api/teams/{teamId}/tasks/templates/...`).
 * This is the only place the picker talks to the server; the picker view
 * consumes these hooks and never calls `apiFetch` directly (the clean
 * query/view split §A7 wants, mirroring `taskNodes.ts` + `useTaskSnapshots.ts`).
 *
 * Issue #985 (Phase, learnability). The frontend picker for the starter
 * templates the backend shipped: a lead with a blank Map adopts a worked
 * example and learns decomposition by example. Three thin hooks:
 *
 *     GET  /api/teams/{teamId}/tasks/templates              → TaskTemplateSummary[]
 *     GET  /api/teams/{teamId}/tasks/templates/{id}         → TaskTemplateRead (with body)
 *     POST /api/teams/{teamId}/tasks/templates/{id}/adopt   → 201 AdoptTemplateResponse (LEAD-only)
 *
 * The list + detail follow the established read conventions (keyed by team /
 * team+template, `retry: false`, a sensible `staleTime`, `enabled` only when
 * the inputs are present). The detail is enabled lazily — only when a template
 * is selected for preview — so opening the picker fetches the cheap summaries
 * and the heavier `body` lands only on demand.
 *
 * Adopt clones the template into the team as NEW ROOT nodes (server-side). On
 * success it **invalidates the task-nodes query** (`TASK_NODES_KEY`, the very
 * key `useTaskNodes` reads) so the canvas re-derives populated with no manual
 * refetch — the empty workspace gives way to the live tree, and the #980
 * next-step coach then guides the lead's next move.
 *
 * Graceful degradation: a given environment may not have the templates
 * endpoints deployed. `retry: false` + an exposed `isError` lets the picker
 * render a calm "examples aren't available yet" state rather than spin or
 * crash. A non-array list body degrades to an empty list.
 *
 * Brand firewall (§A9): templates teach the WORK's shape — they carry only
 * title/description/structure, never anything about a person, a tally, or a
 * tier. Nothing in this module derives or surfaces such a thing.
 *
 * Isolation: imports only the generic `apiFetch` + the generated schema types
 * and the task-nodes cache key — no scheduler coupling (`taskmap ⊥ scheduler`).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "../../api/client";
import type { components } from "../../api/schema.gen";
import { TASK_NODES_KEY } from "./taskNodes";

export type TaskTemplateSummary = components["schemas"]["TaskTemplateSummary"];
export type TaskTemplateRead = components["schemas"]["TaskTemplateRead"];
export type TemplateNode = components["schemas"]["TemplateNode"];
type AdoptTemplateResponse = components["schemas"]["AdoptTemplateResponse"];

/** Cache key for a team's template list. Exported for tests + invalidation. */
export const TASK_TEMPLATES_KEY = (teamId: string) => ["task-templates", teamId] as const;
/** Cache key for one template's full body (the preview). */
export const TASK_TEMPLATE_KEY = (teamId: string, templateId: string) =>
  ["task-template", teamId, templateId] as const;

const STALE_TIME_MS = 5 * 60_000;

function templatesBase(teamId: string): string {
  return `/api/teams/${teamId}/tasks/templates`;
}

export interface UseTaskTemplatesResult {
  /** The library's worked-example summaries (server order). Empty while loading / on error. */
  readonly templates: TaskTemplateSummary[];
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed 404). */
  readonly isError: boolean;
}

/**
 * The team-scoped template library — the worked examples the lead can adopt.
 * `enabled` is the caller's switch: pass `false` (default) until the picker is
 * shown so we don't fetch on the empty workspace until the lead opens it.
 * Degrades to an empty list on a non-array / errored body so the picker shows
 * the calm empty/unavailable state.
 */
export function useTaskTemplates(
  teamId: string | undefined,
  enabled = true,
): UseTaskTemplatesResult {
  const query = useQuery({
    queryKey: TASK_TEMPLATES_KEY(teamId ?? ""),
    enabled: Boolean(teamId) && enabled,
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<TaskTemplateSummary[]>(templatesBase(teamId as string)),
  });

  const templates = Array.isArray(query.data) ? query.data : [];
  return { templates, isLoading: query.isLoading, isError: query.isError };
}

export interface UseTaskTemplateResult {
  /** The full template (summary fields + the worked-example `body` tree), or null. */
  readonly template: TaskTemplateRead | null;
  readonly isLoading: boolean;
  /** True on any fetch failure (incl. a not-yet-deployed 404). */
  readonly isError: boolean;
}

/**
 * One template's full body, for the in-picker tree preview. `enabled` only
 * when both a team + a selected template id are present, so the heavier `body`
 * fetch fires lazily — exactly when the lead expands a row to preview it.
 */
export function useTaskTemplate(
  teamId: string | undefined,
  templateId: string | undefined,
): UseTaskTemplateResult {
  const query = useQuery({
    queryKey: TASK_TEMPLATE_KEY(teamId ?? "", templateId ?? ""),
    enabled: Boolean(teamId) && Boolean(templateId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<TaskTemplateRead>(`${templatesBase(teamId as string)}/${templateId}`),
  });

  return {
    template: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Adopt a template into the team: POST clones its tree as new ROOT nodes
 * (lead-only, enforced server-side). On success the **task-nodes query is
 * invalidated** (`TASK_NODES_KEY` — the exact key `useTaskNodes` reads) so the
 * canvas re-renders populated without a manual refetch: the empty workspace
 * gives way to the live tree, and auto-layout places the cloned nodes (§A7 —
 * adopt creates nodes, layout positions them; no x/y is ever set here).
 *
 * Returns the full `UseMutationResult` so the picker can read `isPending` for
 * the inline Adopt button and `onSuccess` to close itself.
 */
export function useAdoptTemplate(teamId: string | undefined) {
  const qc = useQueryClient();
  const team = teamId ?? "";
  return useMutation({
    mutationFn: (templateId: string) =>
      apiFetch<AdoptTemplateResponse>(`${templatesBase(teamId as string)}/${templateId}/adopt`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: TASK_NODES_KEY(team) }),
  });
}
